import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { familyEntryByRole } from '@eneco/cms-sanity';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { NotificationBox as SparkyNotificationBox } from '@eneco/sparky';
import type { HeadingProps, NotificationBoxProps } from '@eneco/sparky/types';

import type { NotificationBoxContent } from './notification-box-content.types';

/** The legacy component's fallback when the content carries no variant. */
const DEFAULT_VARIANT = 'warning';

/**
 * `NotificationBox` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/NotificationBox/NotificationBox.tsx`, including its
 * `isAlert={false}`.
 *
 * Known simplification: the legacy component renders through `TrackedNotificationBox`
 * (`@eneco/components`), which wraps Sparky's `NotificationBox` in `TrackViewComponent` for view
 * tracking. That wrapper calls `useGTM`, which throws outside a `GTMProvider` — and this lane mounts
 * none, because `lib:tracking` is not in `lib:cms-components`'s allowed dependencies
 * (`eslint.config.mjs`). Rendering Sparky's `NotificationBox` directly is therefore the same trade
 * `TopTaskCTA` makes: the view-tracking event is lost, the box itself renders unchanged.
 *
 * The notification itself arrives differently per lane and is normalised here: Sitecore gives a
 * single `notification` field, which ADR-0068 decision 8 swept into the shared `notifications` family
 * array on the Sanity side, keyed by `role === 'notification'`.
 *
 * `showNotificationOption` is content on both lanes (a Sitecore checkbox field, a Sanity boolean), so
 * it is read via `useContent`; `headingLevel` is a Sitecore rendering parameter and is read via
 * `useSetting`, matching every other migrated block's appearance settings.
 */
const NotificationBox: FC = () => {
  const { adapter } = useCms();
  const content = useContent<NotificationBoxContent>();
  const headingLevel = useSetting('headingLevel') as Exclude<HeadingProps['as'], 'h1'>;

  if (!content?.showNotificationOption) return null;

  const notification = content.notification ?? familyEntryByRole(content.notifications, 'notification');
  if (!notification) return null;

  const title = notification.title as string | undefined;
  const html = adapter.toRichText(notification.content);
  const variant = (notification.variant as NotificationBoxProps['variant']) || DEFAULT_VARIANT;

  return (
    <SparkyNotificationBox
      title={title}
      text={html ? <RichTextComponent html={html} /> : undefined}
      variant={variant}
      headingLevel={headingLevel}
      isAlert={false}
    />
  );
};

export default NotificationBox;
